import type { RootState } from '@app/types';

import { generateProjectsStoreStateProject } from '../../ProjectsList/__fixtures__/project';
import { generateDownloadDataStoreStateExportConfig } from './exportConfig';

export const generateDownloadDataStoreState = (
    overwrite?: Partial<RootState['downloadData']>,
): RootState['downloadData'] => ({
    project: generateProjectsStoreStateProject({
        name: 'First project',
        id: '2364302260e5ca0232c1f4.76524453',
        langs: [
            {
                id: '640',
                name: 'English',
                isDefault: true,
                isEditable: true,
                langIso: 'en',
                ccIso: 'us',
                plural: null,
                stats: {
                    wordCount: 191,
                    percentTranslatedTotal: 86.66666666666667,
                    wordCountUntranslated: 0,
                    calcIsFuzzy: 1,
                },
            },
        ],
        availableRefLang: null,
        projectContributors: null,
        allKeysCount: null,
        isBranchingEnabled: false,
        offlineTranslation: false,
        mau: null,
        branches: null,
        isAdmin: false,
        isCollapsed: false,
        adminRights: {
            upload: false,
            download: false,
            tasks: false,
            contributors: false,
            screenshots: false,
            manageKeys: false,
            manageLanguages: false,
            settings: false,
            createBranches: false,
            activity: false,
            statistics: false,
        },
        stats: {
            percentTranslatedTotal: 22.220767888307158,
            baseWordCount: 191,
            qaTotalCount: 149,
            display: {
                /* eslint-disable @typescript-eslint/naming-convention */
                qa_not_proofread: 120,
                calc_is_fuzzy: 26,
                calc_qa_spelling_and_grammar: 1,
                calc_qa_mismatch_placeholder: 1,
                calc_qa_mismatch_html: 1,
                /* eslint-enable @typescript-eslint/naming-convention */
            },
        },
    }),
    showReviewExport: false,
    allKeysCount: 0,
    distinctPlatform: '',
    fileFormats: [
        {
            group: 'Apple',
            formats: [
                {
                    platform: 1,
                    fallbackFilename: 'Localizable.strings',
                    ext: 'strings',
                    name: 'Lokalise iOS SDK',
                    value: 11,
                    label: 'Lokalise iOS SDK',
                },
                {
                    platform: 1,
                    fallbackFilename: 'Localizable.strings',
                    ext: 'strings',
                    name: 'Apple Strings',
                    value: 10,
                    label: 'Apple Strings (.strings, .stringsdict)',
                },
                {
                    platform: 1,
                    fallbackFilename: 'no_filename.xliff',
                    ext: 'xliff',
                    name: 'Apple XLIFF',
                    value: 20,
                    label: 'Apple XLIFF (.xliff)',
                },
                {
                    platform: 1,
                    fallbackFilename: 'no_filename.plist',
                    ext: 'plist',
                    name: 'Apple Property List',
                    value: 25,
                    label: 'Apple Property List (.plist)',
                },
            ],
        },
        {
            group: 'Android',
            formats: [
                {
                    platform: 2,
                    fallbackFilename: 'strings.xml',
                    ext: 'xml',
                    name: 'Lokalise Android SDK',
                    value: 12,
                    label: 'Lokalise Android SDK',
                },
                {
                    platform: 2,
                    fallbackFilename: 'strings.xml',
                    ext: 'xml',
                    name: 'Android Resources',
                    value: 30,
                    label: 'Android Resources (.xml)',
                },
                {
                    platform: 2,
                    fallbackFilename: 'no_filename.properties',
                    ext: 'properties',
                    name: 'Java Properties',
                    value: 60,
                    label: 'Java Properties (.properties)',
                },
            ],
        },
        {
            group: 'Web',
            formats: [
                {
                    platform: 4,
                    fallbackFilename: 'no_filename.xlf',
                    ext: 'xlf',
                    name: 'Angular i18n XLIFF',
                    value: 94,
                    label: 'Angular i18n XLIFF (.xlf)',
                },
                {
                    platform: 4,
                    fallbackFilename: 'no_filename.po',
                    ext: 'po',
                    name: 'Gettext',
                    value: 80,
                    label: 'Gettext (.po)',
                },
                {
                    platform: 4,
                    fallbackFilename: 'no_filename.json',
                    ext: 'json',
                    name: 'JSON',
                    value: 70,
                    label: 'JSON (.json)',
                },
                {
                    platform: 4,
                    fallbackFilename: 'no_filename.json',
                    ext: 'json',
                    name: 'JSON',
                    value: 72,
                    label: 'Structured JSON (.json)',
                },
                {
                    platform: 4,
                    fallbackFilename: 'no_filename.json',
                    ext: 'js',
                    name: 'JavaScript Object',
                    value: 75,
                    label: 'JavaScript Object (.js)',
                },
                {
                    platform: 4,
                    fallbackFilename: 'no_filename.php',
                    ext: 'php',
                    name: 'PHP Array',
                    value: 98,
                    label: 'PHP Array (.php)',
                },
                {
                    platform: 4,
                    fallbackFilename: 'no_filename.php',
                    ext: 'php',
                    name: 'PHP Laravel Array',
                    value: 97,
                    label: 'PHP Laravel Array (.php)',
                },
                {
                    platform: 4,
                    fallbackFilename: 'no_filename.xliff',
                    ext: 'xliff',
                    name: 'PHP Symfony XLIFF',
                    value: 93,
                    label: 'PHP Symfony XLIFF (.xliff)',
                },
                {
                    platform: 4,
                    fallbackFilename: 'no_filename.ini',
                    ext: 'ini',
                    name: 'PHP INI',
                    value: 96,
                    label: 'PHP INI (.ini)',
                },
                {
                    platform: 4,
                    fallbackFilename: 'no_filename.yml',
                    ext: 'yml',
                    name: 'YAML',
                    value: 110,
                    label: 'YAML (.yml, .yaml)',
                },
                {
                    platform: 4,
                    fallbackFilename: 'no_filename.yml',
                    ext: 'yml',
                    name: 'Ruby on Rails YAML',
                    value: 111,
                    label: 'Ruby on Rails YAML (.yml, .yaml)',
                },
            ],
        },
        {
            group: 'Other',
            formats: [
                {
                    platform: 16,
                    fallbackFilename: 'no_filename.resx',
                    ext: 'resx',
                    name: '.NET Resource',
                    value: 50,
                    label: '.NET Resource (.resx)',
                },
                {
                    platform: 16,
                    fallbackFilename: 'no_filename.csv',
                    ext: 'csv',
                    name: 'CSV',
                    value: 130,
                    label: 'CSV (.csv)',
                },
                {
                    platform: 16,
                    fallbackFilename: 'no_filename.xlsx',
                    ext: 'xlsx',
                    name: 'Excel',
                    value: 100,
                    label: 'Excel (.xlsx)',
                },
                {
                    platform: 16,
                    fallbackFilename: 'no_filename.xlsx',
                    ext: 'xlsx',
                    name: 'Excel',
                    value: 180,
                    label: 'Multilingual Excel (.xlsx)',
                },
                {
                    platform: 16,
                    fallbackFilename: 'no_filename.stf',
                    ext: 'stf',
                    name: 'Salesforce Translation',
                    value: 120,
                    label: 'Salesforce Translation (.stf)',
                },
                {
                    platform: 16,
                    fallbackFilename: 'no_filename.ts',
                    ext: 'ts',
                    name: 'Qt Linguist',
                    value: 140,
                    label: 'Qt Linguist (.ts)',
                },
                {
                    platform: 16,
                    fallbackFilename: 'no_filename.js',
                    ext: 'js',
                    name: 'React Native',
                    value: 76,
                    label: 'React Native (i18n)',
                },
                {
                    platform: 16,
                    fallbackFilename: 'no_filename.js',
                    ext: 'arb',
                    name: 'Flutter',
                    value: 200,
                    label: 'Flutter (.arb)',
                },
                {
                    platform: 16,
                    fallbackFilename: 'no_filename.srt',
                    ext: 'srt',
                    name: 'SubRip',
                    value: 210,
                    label: 'SubRip (.srt)',
                },
            ],
        },
    ],
    exportConfig: generateDownloadDataStoreStateExportConfig(),
    unassignedKeysPerPlatformFilename: {
        /* eslint-disable @typescript-eslint/naming-convention */
        all_1: '14',
        all_2: '15',
        all_4: '4',
        all_16: '14',
        limit_1: '3',
        limit_2: '1',
        limit_4: '0',
        limit_16: '3',
        /* eslint-enable @typescript-eslint/naming-convention */
    },
    placeholderFormatDefaults: {
        '10': 1,
        '11': 1,
        '12': 0,
        '15': 1,
        '20': 1,
        '25': 0,
        '30': 0,
        '40': 3,
        '50': 3,
        '60': 0,
        '70': 0,
        '72': 0,
        '75': 0,
        '76': 0,
        '80': 0,
        '90': 0,
        '93': 4,
        '94': 5,
        '95': 0,
        '96': 0,
        '97': 0,
        '98': 0,
        '100': 0,
        '110': 0,
        '111': 0,
        '120': 0,
        '130': 0,
        '140': 0,
        '170': 4,
        '200': 2,
    },
    pluralFormatDefaults: {
        '10': 0,
        '11': 0,
        '12': 0,
        '15': 0,
        '20': 0,
        '25': 1,
        '30': 0,
        '40': 1,
        '50': 1,
        '60': 1,
        '70': 4,
        '72': 4,
        '75': 1,
        '76': 4,
        '80': 0,
        '90': 0,
        '93': 3,
        '94': 2,
        '95': 1,
        '96': 1,
        '97': 1,
        '98': 1,
        '100': 1,
        '110': 4,
        '111': 4,
        '120': 1,
        '130': 1,
        '140': 0,
        '170': 3,
        '200': 2,
    },
    indentationDefaults: {
        '10': 2,
        '15': 2,
        '20': 9,
        '25': 9,
        '30': 2,
        '50': 9,
        '70': 4,
        '72': 4,
        '75': 4,
        '76': 4,
        '93': 9,
        '94': 9,
        '110': 4,
        '111': 2,
        '140': 9,
        '170': 9,
    },
    baseLangIso: 'en',
    baseLangName: 'English',
    exportEmptyOptions: [
        {
            value: 'empty',
            label: 'Empty',
        },
        {
            value: 'skip',
            label: 'Skip',
        },
    ],
    exportNullAsOptions: [
        {
            value: 'empty',
            label: 'Empty',
        },
        {
            value: 'null',
            label: 'Null',
        },
    ],
    exportSortOptions: [
        { value: 'first_added', label: 'First added' },
        { value: 'last_added', label: 'Last added' },
    ],
    javaPropertiesEncodingOptions: [
        {
            value: 'latin-1',
            label: 'latin-1',
        },
        {
            value: 'utf-8',
            label: 'utf-8',
        },
    ],
    javaPropertiesSeparatorOptions: [
        { value: 'auto', label: 'auto' },
        { value: '=', label: '=' },
        { value: ':', label: ':' },
    ],
    pluralFormatSelect: [
        { value: 0, text: 'Default plural format' },
        { value: 1, text: 'JSON String' },
        { value: 2, text: 'ICU' },
        { value: 4, text: 'Array' },
        { value: 5, text: 'i18next' },
        { value: 3, text: 'Symfony' },
    ],
    pluralFormatWhitelist: {
        '10': [0],
        '11': [0],
        '12': [0],
        '15': [0],
        '20': [0, 1, 2],
        '25': [1, 2],
        '30': [0],
        '40': [1, 2],
        '50': [1, 2],
        '60': [1, 2],
        '70': [1, 2, 4, 5, 3],
        '72': [1, 2, 4, 5, 3],
        '75': [1, 2, 4, 5, 3],
        '76': [1, 2, 4, 5],
        '80': [0],
        '90': [0],
        '93': [1, 2, 3],
        '94': [1, 2],
        '95': [1, 2, 4, 3],
        '96': [1, 2],
        '97': [1, 2, 4],
        '98': [1, 2, 4, 3],
        '100': [1, 2],
        '110': [1, 2, 4, 3],
        '111': [1, 2, 4],
        '120': [1, 2],
        '130': [1, 2],
        '140': [0],
        '170': [1, 2, 3],
        '200': [2],
    },
    placeholderFormatSelect: [
        { value: 0, text: 'Printf' },
        { value: 1, text: 'iOS' },
        { value: 2, text: 'ICU' },
        { value: 3, text: '.NET' },
        { value: 4, text: 'Symfony' },
        { value: 5, text: 'i18n' },
        { value: 6, text: 'Raw' },
    ],
    placeholderFormatWhitelist: {
        '10': [1],
        '11': [1],
        '12': [0],
        '15': [1],
        '20': [0, 1, 2, 6],
        '25': [0, 1, 2],
        '30': [0],
        '40': [3],
        '50': [3],
        '60': [0, 2, 3],
        '70': [0, 1, 2, 3, 4, 5, 6],
        '72': [0, 1, 2, 3, 4, 5, 6],
        '75': [0, 2, 4, 5, 6],
        '76': [0, 1, 2, 5, 6],
        '80': [0, 2, 6],
        '90': [0, 2, 6],
        '93': [0, 2, 4, 6],
        '94': [0, 2, 5, 6],
        '95': [0, 2, 4, 5, 6],
        '96': [0, 2],
        '97': [0, 2],
        '98': [0, 2, 4, 5, 6],
        '100': [0, 1, 2, 3, 5, 6],
        '110': [0, 2, 4, 5, 6],
        '111': [0, 2],
        '120': [0, 2],
        '130': [0, 1, 2, 3, 5, 6],
        '140': [0, 2],
        '170': [0, 2, 4, 6],
        '200': [2],
    },
    indentationSelect: {
        '1': '1 space',
        '2': '2 spaces',
        '3': '3 spaces',
        '4': '4 spaces',
        '5': '5 spaces',
        '6': '6 spaces',
        '7': '7 spaces',
        '8': '8 spaces',
        '9': 'Tab',
    },
    indentationBlacklist: { '110': [9], '111': [9] },
    availableProjects: [
        { id: '2364302260e5ca0232c1f4.76524453', insertId: '135977', name: 'First project' },
        { id: '7052098360eeeac5f02956.17891556', insertId: '135978', name: 'Second project' },
    ],
    projectBranchName: 'master',
    availableFilenames: [
        { filename: '%LANG_ISO%.json', device: 4, format: 70 },
        { filename: 'Localizable.strings', device: 1, format: 10 },
        { filename: 'all.json', device: 4, format: 70 },
    ],
    customTranslationStatuses: {
        '9777977860c35ec49cc4b1.15807104': { projectTitle: 'Sample Project', statuses: [] },
        '2364302260e5ca0232c1f4.76524453': {
            projectTitle: 'Project for filling space',
            statuses: [
                {
                    id: 'someCustomTranslationStatusId',
                    title: 'Custom translation status title',
                    color: 'red',
                },
            ],
        },
        '7052098360eeeac5f02956.17891556': { projectTitle: 'Key assignment project', statuses: [] },
        '41206616611e173af01aa9.34832794': { projectTitle: 'Empty project', statuses: [] },
    },
    dataRange: [
        { value: 'exportAll', label: 'All', isChecked: true },
        { value: 'exportGuntranslated', label: 'Untranslated strings', isChecked: false },
        { value: 'exportGtranslated', label: 'Translated strings', isChecked: false },
        { value: 'exportGproofread', label: 'Reviewed-only strings', isChecked: false },
        { value: 'exportGlastReviewed', label: 'Last reviewed strings', isChecked: false },
        { value: 'exportGnonfuzzy', label: 'Verified strings', isChecked: false },
        { value: 'exportGnonhidden', label: 'Non-hidden keys', isChecked: false },
    ],
    tags: ['api', 'appstore', 'frontend'],
    integrations: {
        azure: true,
        amazons3: false,
        gcs: false,
        gitHub: false,
        gitHubEnterprise: false,
        gitLab: false,
        bitBucket: false,
        bitBuckEtenterprise: false,
    },
    repositories: {
        azure: [],
        amazons3: [],
        gcs: [],
        github: [],
        githubEnterprise: [],
        gitlab: [],
        bitbucket: [],
        bitbucketEnterprise: [],
    },
    customTranslationStatusesAvailable: false,
    enabledTriggers: {
        triggersAmazons3: false,
        triggersGcs: false,
        triggersGithub: false,
        triggersGithubEnterprise: false,
        triggersGitlab: false,
        triggersBitbucket: false,
        triggersBitbucketEnterprise: false,
        triggersAzure: false,
    },
    integrationsConfig: {
        azure: { title: '', description: '' },
        amazons3: { title: '', description: '' },
        gcs: { title: '', description: '' },
        gitHub: { title: '', description: '' },
        gitHubEnterprise: { title: '', description: '' },
        gitLab: { title: '', description: '' },
        bitBucket: { title: '', description: '' },
        bitBuckEtenterprise: { title: '', description: '' },
    },
    ...overwrite,
});

const downloadData = generateDownloadDataStoreState();

export default downloadData;
